import type { GtmBrain } from "../api/GtmApi";
import type { BrainEditorSectionId } from "./GtmBrainEditorPanel";

export type MarketProfileDocId = "overview" | "icp" | "company";

export type MarketProfileDoc = {
  id: MarketProfileDocId;
  label: string;
  description: string;
  /** Editor accordion to open */
  sectionId: BrainEditorSectionId;
  icon: "overview" | "icp" | "company";
  filled: number;
  total: number;
};

function filledCount(checks: boolean[]): { filled: number; total: number } {
  return { filled: checks.filter(Boolean).length, total: checks.length };
}

function hasText(value?: string | null): boolean {
  return !!value && value.trim().length > 0;
}

function hasList(value?: string[] | null): boolean {
  return !!value && value.some((v) => v.trim().length > 0);
}

export function marketProfileDocs(brain: GtmBrain): MarketProfileDoc[] {
  const overview = filledCount([
    hasText(brain.valueProposition),
    hasText(brain.businessSummary),
    hasText(brain.offering),
    hasText(brain.keyFeatures),
    hasText(brain.socialProof),
    hasList(brain.industries),
    hasList(brain.geographies),
  ]);
  const icp = filledCount([
    hasText(brain.icpSummary),
    hasText(brain.targetCompanySize),
    hasList(brain.verticals),
    hasList(brain.buyingRoles),
    hasList(brain.championRoles),
    hasList(brain.userRoles),
  ]);
  const company = filledCount([
    hasText(brain.companyName),
    hasText(brain.domain),
    hasText(brain.linkedinUrl),
    hasText(brain.companySize),
    hasText(brain.businessModel),
    hasText(brain.stage),
    hasText(brain.language),
  ]);

  return [
    {
      id: "overview",
      label: "Overview",
      description: "What you do, why it matters, and the evidence behind it.",
      sectionId: "market",
      icon: "overview",
      filled: overview.filled,
      total: overview.total,
    },
    {
      id: "icp",
      label: "Ideal customer profile",
      description: "Who buys and who champions the deal.",
      sectionId: "icp",
      icon: "icp",
      filled: icp.filled,
      total: icp.total,
    },
    {
      id: "company",
      label: "Company",
      description: "Identity, size, stage, and presence.",
      sectionId: "company",
      icon: "company",
      filled: company.filled,
      total: company.total,
    },
  ];
}

export const MARKET_PROFILE_SUBNAV = [
  { id: "overview" as const, label: "Overview" },
  { id: "icp" as const, label: "Ideal customer profile" },
  { id: "company" as const, label: "Company" },
];
